import { useGPFormContext } from '@/contexts/GPFormContext';
import { CHECKBOX_GROUP_TYPE } from '@/types/forms/fields';
import { FormSectionType } from '@/types/forms';
import { IconButton, Tooltip } from '@mui/material';
import { ArrowSwapHorizontal } from 'givepulse-iconsax-react';
import { useTranslation } from 'react-i18next';

type GPFilterReverseButtonProps = {
  /** The filter section whose checkbox selections should be inverted. */
  button: FormSectionType;
};

const asStringSet = (value: unknown): Set<string> =>
  new Set((Array.isArray(value) ? value : []).map(String));

/**
 * Inverts the current selection of every checkbox-group field in a filter section:
 * the selected options become deselected and vice-versa (client-side, over the options
 * currently shown). The complement is then sent as a normal include filter, so no
 * backend negation is required.
 */
const GPFilterReverseButton = ({ button }: GPFilterReverseButtonProps) => {
  const { getValues, setValue } = useGPFormContext();
  const { t } = useTranslation();

  const reverse = () => {
    button.fields.forEach(field => {
      if (field.type !== CHECKBOX_GROUP_TYPE) return;

      const selected = asStringSet(getValues(field.name));
      const complement = field.options
        .map(option => String(option.name))
        .filter(optionName => !selected.has(optionName));

      setValue(field.name, complement.length ? complement : undefined, { shouldDirty: true });
    });
  };

  return (
    <Tooltip title={t('Reverse selection')}>
      <IconButton size="small" color="primary" aria-label={t('Reverse selection')} onClick={reverse}>
        <ArrowSwapHorizontal size={18} />
      </IconButton>
    </Tooltip>
  );
};

export default GPFilterReverseButton;
